import React from 'react';
import { FiArrowLeft, FiShield, FiLock, FiEye, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-green-600 hover:text-green-700 mr-4"
            >
              <FiArrowLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
          
          {/* Introduction */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FiShield className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Your Privacy Matters</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Mandi Live ("we," "our," or "us") operates the mobile application and website at mandilive.in 
              (the "Service"). This page informs you of our policies regarding the collection, use, and 
              disclosure of personal data when you use our Service and the choices you have associated with that data.
            </p>
          </div>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiEye className="w-5 h-5 text-blue-600" />
              Information We Collect
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Personal Information</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  When you use our app, we may collect the following types of personal information:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Phone number (for registration and notifications)</li>
                  <li>Location data (to provide local mandi prices and weather information)</li>
                  <li>Device information (device model, operating system, unique device identifiers)</li>
                  <li>Usage data (how you interact with our app features)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Agricultural Data</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Crop preferences and farming interests</li>
                  <li>Mandi price inquiries and searches</li>
                  <li>Video viewing history for educational content</li>
                  <li>Government scheme applications or interests</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Log files and crash reports</li>
                  <li>IP address and network information</li>
                  <li>App performance and analytics data</li>
                  <li>Push notification tokens</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the collected information for various purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>To provide and maintain our farming solution services</li>
              <li>To send you real-time mandi price updates and notifications</li>
              <li>To provide location-based weather and market information</li>
              <li>To improve our app functionality and user experience</li>
              <li>To provide customer support and respond to your inquiries</li>
              <li>To send you educational content about farming practices</li>
              <li>To notify you about government schemes relevant to farmers</li>
              <li>To analyze usage patterns and improve our services</li>
              <li>To ensure the security and integrity of our app</li>
            </ul>
          </section>

          {/* Data Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiLock className="w-5 h-5 text-red-600" />
              Data Sharing and Disclosure
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers who help us operate our app (analytics, cloud storage, push notifications)</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to valid legal requests</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets, your information may be transferred</li>
              <li><strong>Consent:</strong> We may share your information with your explicit consent</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. However, please 
              note that no method of transmission over the internet or electronic storage is 100% secure.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> You can request access to your personal data we hold</li>
              <li><strong>Correction:</strong> You can request correction of inaccurate personal data</li>
              <li><strong>Deletion:</strong> You can request deletion of your personal data</li>
              <li><strong>Portability:</strong> You can request a copy of your data in a machine-readable format</li>
              <li><strong>Withdraw Consent:</strong> You can withdraw consent for data processing at any time</li>
              <li><strong>Opt-out:</strong> You can opt-out of marketing communications</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our app may use third-party services that collect information used to identify you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Firebase:</strong> For authentication, database, and push notifications</li>
              <li><strong>Google Analytics:</strong> For app usage analytics</li>
              <li><strong>Weather APIs:</strong> For providing weather information</li>
              <li><strong>Government APIs:</strong> For fetching scheme and policy information</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              These third-party services have their own privacy policies. We encourage you to review them.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information only for as long as necessary to fulfill the purposes 
              outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. 
              When we no longer need your personal information, we will securely delete or anonymize it.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service is not intended for use by children under the age of 13. We do not knowingly 
              collect personally identifiable information from children under 13. If we discover that a 
              child under 13 has provided us with personal information, we will delete such information immediately.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiMail className="w-5 h-5 text-green-600" />
              Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Website:</strong> mandilive.in</p>
             
            </div>
          </section>

          {/* Effective Date */}
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              This Privacy Policy is effective as of {new Date().toLocaleDateString()} and applies to all users of the Mandi Live application and mandilive.in website.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
